import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCarsIfNeeded,
  getActiveBooking,
  getTimelineStatusChanges,
  selectActiveBookings,
  selectCarsLoading,
  selectDisplayCars,
  selectTimelineStatusChanges,
} from '../../../redux/store/userData/cars';
import { selectAreas } from '../../../redux/store/appData/areas/selectors';
import {
  fetchFleetsIfNeeded,
  selectFleets,
} from '../../../redux/store/appData/fleets';
import {
  fetchCategoriesIfNeeded,
  selectCategories,
} from '../../../redux/store/userData/categories';
import {
  getTimelineTrips,
  selectTimelineTrips,
} from '../../../redux/store/userData/trips';
import { DAYS_BEFORE_TODAY } from '../constants';
import { CarLike } from '../types';
import { Booking } from '../utils/bookingTiles';
import { StatusChange } from '../utils/statusPeriods';

const DAY_MS = 24 * 60 * 60 * 1000;

type TimelineData = {
  cars: CarLike[];
  areas: any;
  // Lookups for the "Group by" picker's group names (fleet / category).
  fleets: any;
  categories: any;
  loading: boolean;
  bookings: Booking[];
  // Vehicle status-change history (for the period background bands).
  statusChanges: StatusChange[];
  // Past trips (finished + cancelled) over the visible window, mapped to the tile
  // Booking shape. Merged with `bookings` only for tile building (see VehicleTimeline).
  tripBookings: Booking[];
};

// The SINGLE data-loading entry point for the timeline. Any future timeline data
// (status changes, etc.) is added HERE, not scattered across components.
const useTimelineData = (): TimelineData => {
  const dispatch = useDispatch();
  const cars = (useSelector(selectDisplayCars) ?? []) as unknown as CarLike[];
  const areas = useSelector(selectAreas);
  const fleets = useSelector(selectFleets);
  const categories = useSelector(selectCategories);
  const loading = useSelector(selectCarsLoading);
  const bookings = (useSelector(selectActiveBookings) ?? []) as Booking[];
  const statusChanges = (useSelector(selectTimelineStatusChanges) ??
    []) as StatusChange[];
  const timelineTrips = (useSelector(selectTimelineTrips) ?? []) as any[];

  // dedup-safe — no-op if already loaded by SideMenu/login/other tables.
  useEffect(() => {
    dispatch(fetchCarsIfNeeded() as any);
    dispatch(fetchFleetsIfNeeded() as any);
    dispatch(fetchCategoriesIfNeeded() as any);
    // Past finished trips for the timeline window. Refetched on every open (this hook
    // mounts with VehicleTimeline); the thunk fetches closest-to-today first and streams
    // pages in via append. The last ~2 weeks are persisted, so they repaint instantly
    // while the rest streams in.
    dispatch(getTimelineTrips() as any);
  }, [dispatch]);

  // Refetch active bookings whenever the displayed car set changes. Keyed by a
  // stable signature so we don't refetch on every render / unrelated car edits.
  const carIdsSignature = useMemo(
    () =>
      cars
        .map((c) => c.id)
        .sort()
        .join(','),
    [cars]
  );

  useEffect(() => {
    if (!carIdsSignature) return;
    const ids = carIdsSignature.split(',');
    dispatch(getActiveBooking(ids) as any);
    // Status-change history over the rendered range (history is past-only, so
    // fetch from the range start up to now; the trailing period extends forward).
    const now = new Date();
    const from = new Date(
      now.getTime() - DAYS_BEFORE_TODAY * DAY_MS
    ).toISOString();
    dispatch(
      getTimelineStatusChanges(from, now.toISOString(), ids, () => {}) as any
    );
  }, [dispatch, carIdsSignature]);

  // Map each past trip → the tile Booking shape (reusing buildTilesByCar). from =
  // tripStart (fallback reservedAt); to = tripEnd || backBy (fallback start). The tile
  // builder colours cancelled trips grey via `status`.
  const tripBookings = useMemo<Booking[]>(
    () =>
      timelineTrips
        .filter((t) => t && t.carId && t.id && (t.tripStart || t.reservedAt))
        .map((t) => {
          const from = t.tripStart || t.reservedAt;
          // Use the trip's real end; a zero-length trip (tripEnd === tripStart)
          // falls back to the reserved return `backBy` so the tile stays visible.
          const realEnd =
            t.tripEnd && t.tripEnd !== t.tripStart
              ? t.tripEnd
              : t.backBy || t.tripEnd || from;
          return {
            id: t.id,
            carId: t.carId,
            from,
            to: realEnd,
            status: t.status,
            type: t.type,
            rentalPresentableId: t.code || t.rentalPresentableId,
            statusChange: false,
          } as Booking;
        }),
    [timelineTrips]
  );

  return {
    cars,
    areas,
    fleets,
    categories,
    loading,
    bookings,
    statusChanges,
    tripBookings,
  };
};

export default useTimelineData;
